import { c as _c } from "react/compiler-runtime";
import { useKeyboardEvent } from '@react-hookz/web/useKeyboardEvent/index.js';
import type { PropsWithChildren } from 'react';
import { Children, useEffect, useMemo, useRef, useState } from 'react';
import { useFocusInside } from '#cutils/hooks/useFocusInside';
import styles from './Tabs.module.scss';
const TabBarKeyboardEvents = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);
type Props = PropsWithChildren<{
  className?: string;
}>;
export function Tabs(t0) {
  const $ = _c(34);
  const {
    children,
    className
  } = t0;
  const [activeTab, setActiveTab] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const tabButtonsRef = useRef(t1);
  let t2;
  if ($[1] !== children) {
    t2 = Children.toArray(children).filter(_temp);
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tabs = t2;
  const {
    focused: tabListIsFocused,
    ref: tablistRef
  } = useFocusInside();
  let t3;
  if ($[3] !== tabListIsFocused) {
    t3 = event => tabListIsFocused && TabBarKeyboardEvents.has(event.code);
    $[3] = tabListIsFocused;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== activeTab || $[6] !== tabs.length) {
    t4 = t5 => {
      const {
        code
      } = t5;
      const lastChildIndex = Math.max(tabs.length - 1, 0);
      let tabToActivate = activeTab;
      bb12: switch (code) {
        case "ArrowLeft":
          {
            tabToActivate = Math.max(activeTab - 1, 0);
            break bb12;
          }
        case "ArrowRight":
          {
            tabToActivate = Math.min(activeTab + 1, lastChildIndex);
            break bb12;
          }
        case "Home":
          {
            tabToActivate = 0;
            break bb12;
          }
        case "End":
          {
            tabToActivate = lastChildIndex;
          }
      }
      setActiveTab(tabToActivate);
    };
    $[5] = activeTab;
    $[6] = tabs.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== activeTab || $[9] !== tabListIsFocused || $[10] !== tabs) {
    t5 = [tabListIsFocused, activeTab, tabs];
    $[8] = activeTab;
    $[9] = tabListIsFocused;
    $[10] = tabs;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useKeyboardEvent(t3, t4, t5);
  let t6;
  let t7;
  if ($[12] !== activeTab || $[13] !== tabListIsFocused) {
    t6 = () => {
      if (tabListIsFocused) {
        tabButtonsRef.current[activeTab]?.focus();
      }
    };
    t7 = [activeTab, tabListIsFocused];
    $[12] = activeTab;
    $[13] = tabListIsFocused;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function handleTabActivate(tabNumber) {
      setActiveTab(tabNumber);
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleTabActivate = t8;
  let t9;
  if ($[17] !== activeTab || $[18] !== tabs) {
    t9 = Children.map(tabs, (child_0, index) => {
      const isActiveTab = activeTab === index;
      return <button id={`tab-${index}`} className="button-link no-focus" type="button" role="tab" aria-selected={isActiveTab} aria-controls={`tabpanel-${index}`} onClick={() => handleTabActivate(index)} tabIndex={isActiveTab ? 0 : -1} ref={element => {
        tabButtonsRef.current[index] = element;
      }}>{(child_0 as any).props.icon}<span className={`${styles.tabButtonLabel} heading4`}>{(child_0 as any).props.label}</span>{index === activeTab ? <div className={styles.activeTabLine} /> : null}</button>;
    });
    $[17] = activeTab;
    $[18] = tabs;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9 || $[21] !== tablistRef) {
    t10 = <div className={styles.tabList} role="tablist" ref={tablistRef}>{t9}</div>;
    $[20] = t9;
    $[21] = tablistRef;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== activeTab || $[24] !== tabs) {
    let t12;
    if ($[26] !== activeTab) {
      t12 = (child_1, index_0) => <div className={`${styles.tabContent} ${activeTab === index_0 ? styles.tabActive : ""}`} id={`tabpanel-${index_0}`} role="tabpanel" tabIndex={-1} aria-labelledby={`tab-${index_0}`}>{child_1}</div>;
      $[26] = activeTab;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t11 = Children.map(tabs, t12);
    $[23] = activeTab;
    $[24] = tabs;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <div className={styles.tabContainer}>{t11}</div>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== className || $[31] !== t10 || $[32] !== t12) {
    t13 = <div className={className}>{t10}{t12}</div>;
    $[30] = className;
    $[31] = t10;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function _temp(child) {
  return !!child;
}